import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader } from '@mantine/core';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { getAuthPageStyle } from './authInputStyles';
import classes from './AuthPage.module.css';

type AuthPageShellProps = {
  title: string;
  subtitle?: string;
  icon: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'default' | 'wide';
  accentColor?: string;
};

export function AuthPageShell({
  title,
  subtitle,
  icon,
  children,
  footer,
  width = 'default',
  accentColor = DEFAULT_QR_CODE_COLOR,
}: AuthPageShellProps) {
  const pageStyle = getAuthPageStyle(accentColor);

  return (
    <div className={classes.page} style={pageStyle}>
      <div
        className={`${classes.card} ${width === 'wide' ? classes.cardWide : classes.cardDefault}`}
        style={pageStyle}
      >
        <header className={classes.header}>
          <div className={classes.headerRow}>
            <div className={classes.iconBox}>{icon}</div>
            <div className={classes.titleBlock}>
              <h1 className={classes.title}>{title}</h1>
              {subtitle ? <p className={classes.subtitle}>{subtitle}</p> : null}
            </div>
          </div>
        </header>
        <div className={classes.body}>
          {children}
          {footer ? <div className={classes.footerNote}>{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

type AuthTextLinkProps = {
  children: ReactNode;
  onClick: () => void;
  accentColor?: string;
};

export function AuthTextLink({
  children,
  onClick,
  accentColor = DEFAULT_QR_CODE_COLOR,
}: AuthTextLinkProps) {
  return (
    <button
      type="button"
      className={classes.textLink}
      style={getAuthPageStyle(accentColor)}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

type AuthPrimaryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  loading?: boolean;
  accentColor?: string;
};

export function AuthPrimaryButton({
  children,
  loading = false,
  disabled,
  accentColor = DEFAULT_QR_CODE_COLOR,
  className,
  type = 'button',
  ...rest
}: AuthPrimaryButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={`${classes.primaryButton} ${className ?? ''}`}
      style={getAuthPageStyle(accentColor)}
      {...rest}
    >
      {loading ? <Loader size={16} color="white" /> : null}
      <span>{children}</span>
    </button>
  );
}

type AuthSubmitButtonProps = {
  children: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  accentColor?: string;
};

export function AuthSubmitButton({
  children,
  loading = false,
  disabled = false,
  accentColor = DEFAULT_QR_CODE_COLOR,
}: AuthSubmitButtonProps) {
  return (
    <div className={classes.submitWrap}>
      <AuthPrimaryButton
        type="submit"
        loading={loading}
        disabled={disabled}
        accentColor={accentColor}
      >
        {children}
      </AuthPrimaryButton>
    </div>
  );
}

type AuthSecondaryButtonProps = {
  children: ReactNode;
  onClick: () => void;
  accentColor?: string;
};

export function AuthSecondaryButton({
  children,
  onClick,
  accentColor = DEFAULT_QR_CODE_COLOR,
}: AuthSecondaryButtonProps) {
  return (
    <button
      type="button"
      className={classes.secondaryButton}
      style={getAuthPageStyle(accentColor)}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
